/**
 * ScopeDocOutline - the Scope doc's hover-dots section nav. A slim sticky rail of dots on the
 * right edge (one per H1, smaller per H2); hovering (or keyboard-focusing) the rail reveals a
 * floating outline panel with the actual titles; clicking either scrolls to the heading -
 * expanding any collapsed ancestor first (the collapse plugin keys on the same heading dataKey).
 *
 * Headings are enumerated from the LIVE editor doc (both modes mount a real editor, read-only in
 * view), refreshed on doc-changing transactions - NOT the 'update' event, which external
 * setContent (emitUpdate:false) never fires. A scrollspy (IntersectionObserver) marks the active
 * section's dot. Renders nothing until the editor exists or with fewer than 2 headings.
 */

import * as React from 'react';
import { expandCollapsedHeadings, headingCollapseKey, type Editor } from '@protoss/ui';

interface OutlineHeading {
  collapseKey: string | null;
  level: number;
  text: string;
  pos: number;
}

/** Minimal structural node view (apps/web has no tiptap dependency). */
interface PmNodeLike {
  type: { name: string };
  attrs?: Record<string, unknown>;
  textContent: string;
}

function collectOutline(editor: Editor): OutlineHeading[] {
  const out: OutlineHeading[] = [];
  (editor.state.doc as unknown as {
    forEach: (fn: (node: PmNodeLike, offset: number) => void) => void;
  }).forEach((node, offset) => {
    if (node.type.name !== 'heading') return;
    const level = Number(node.attrs?.level ?? 1);
    if (level > 2) return;
    const text = node.textContent.trim();
    if (!text) return;
    out.push({
      collapseKey: headingCollapseKey(node as never),
      level,
      text,
      pos: offset,
    });
  });
  return out;
}

export function ScopeDocOutline({ editor }: { editor: Editor | null }): React.ReactElement | null {
  const [headings, setHeadings] = React.useState<OutlineHeading[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Enumerate on mount/editor change + on doc-changing transactions (debounced).
  React.useEffect(() => {
    if (!editor) {
      setHeadings([]);
      return;
    }
    setHeadings(collectOutline(editor));
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      if (!transaction.docChanged) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!editor.isDestroyed) setHeadings(collectOutline(editor));
      }, 250);
    };
    editor.on('transaction', onTransaction);
    return () => {
      if (timer) clearTimeout(timer);
      editor.off('transaction', onTransaction);
    };
  }, [editor]);

  // Scrollspy: active = the lowest-index heading intersecting the top band of the viewport.
  React.useEffect(() => {
    if (!editor || headings.length < 2) return;
    const elements: Array<{ el: Element; index: number }> = [];
    for (let i = 0; i < headings.length; i++) {
      const dom = editor.view.nodeDOM(headings[i]!.pos);
      if (dom instanceof Element) elements.push({ el: dom, index: i });
    }
    if (elements.length === 0) return;
    const visible = new Set<number>();
    const byEl = new Map(elements.map((e) => [e.el, e.index] as const));
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = byEl.get(entry.target);
          if (idx === undefined) continue;
          if (entry.isIntersecting) visible.add(idx);
          else visible.delete(idx);
        }
        if (visible.size > 0) setActiveIndex(Math.min(...visible));
      },
      { rootMargin: '0px 0px -70% 0px' }
    );
    for (const { el } of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [editor, headings]);

  const handleSelect = React.useCallback(
    (index: number) => {
      if (!editor) return;
      // Re-resolve fresh from the live doc (positions may have drifted since the last debounce).
      const fresh = collectOutline(editor);
      const wanted = headings[index];
      if (!wanted) return;
      let target =
        fresh.find((h) => wanted.collapseKey != null && h.collapseKey === wanted.collapseKey) ??
        fresh[Math.min(index, fresh.length - 1)];
      if (!target) return;
      // Expand the collapsed ancestor H1 (an H2 under a folded H1 is display:none) + the target.
      const freshIdx = fresh.indexOf(target);
      const keys: string[] = [];
      if (target.collapseKey) keys.push(target.collapseKey);
      if (target.level === 2) {
        for (let i = freshIdx - 1; i >= 0; i--) {
          if (fresh[i]!.level === 1) {
            if (fresh[i]!.collapseKey) keys.push(fresh[i]!.collapseKey!);
            break;
          }
        }
      }
      expandCollapsedHeadings(editor, keys);
      const pos = target.pos;
      requestAnimationFrame(() => {
        if (editor.isDestroyed) return;
        const dom = editor.view.nodeDOM(pos);
        if (dom instanceof HTMLElement) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          dom.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
      });
    },
    [editor, headings]
  );

  if (!editor || headings.length < 2) return null;

  return (
    <nav className="scope-doc-outline" aria-label="Scope sections">
      <div className="scope-doc-outline__rail">
        {headings.map((h, i) => (
          <button
            key={`${h.collapseKey ?? h.text}-${i}`}
            type="button"
            className={`scope-doc-outline__dot${h.level === 2 ? ' scope-doc-outline__dot--h2' : ''}${i === activeIndex ? ' scope-doc-outline__dot--active' : ''}`}
            aria-label={h.text}
            aria-current={i === activeIndex ? 'true' : undefined}
            onClick={() => handleSelect(i)}
          />
        ))}
      </div>
      <div className="scope-doc-outline__panel">
        {headings.map((h, i) => (
          <button
            key={`${h.collapseKey ?? h.text}-${i}`}
            type="button"
            className={`scope-doc-outline__item${h.level === 2 ? ' scope-doc-outline__item--h2' : ''}${i === activeIndex ? ' scope-doc-outline__item--active' : ''}`}
            onClick={() => handleSelect(i)}
          >
            {h.text}
          </button>
        ))}
      </div>
    </nav>
  );
}
